import {
  MetricRegistry,
  serializeReportFilterUrl,
  type DrilldownResponse,
  type ReportFilterStateV1,
  type ReportMetricId,
  type VideoSelection,
} from "@meta-video/contracts";
import { Alert, Button, Loader, Stack, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useLayoutEffect } from "react";

import {
  AccessDeniedError,
  PROTECTED_QUERY_KEY,
  SessionExpiredError,
} from "../../shared/api/client.js";
import type { ReportApi } from "./api.js";
import { isVideoCoverageMetric } from "./columns.js";
import { formatMetricValue, NoDataCell } from "./format.js";
import { reportingEnvelopeFingerprint } from "./reportingEnvelope.js";

export interface VideoAdsPanelProps {
  readonly api: ReportApi;
  readonly columnIds: readonly ReportMetricId[];
  readonly expectedFingerprint?: string;
  readonly filters: ReportFilterStateV1;
  readonly onAccessChanged?: () => void;
  readonly onSessionExpired?: () => void;
  readonly selection: VideoSelection;
  readonly videoMetricCoverageThreshold: number;
}

export function VideoAdsPanel({
  api,
  columnIds,
  expectedFingerprint,
  filters,
  onAccessChanged,
  onSessionExpired,
  selection,
  videoMetricCoverageThreshold,
}: VideoAdsPanelProps) {
  const query = useQuery({
    queryFn: ({ signal }) => api.getDrilldown(
      { filters, selection },
      { signal },
    ),
    queryKey: [
      ...PROTECTED_QUERY_KEY,
      "report",
      "drilldown",
      selection.brandId,
      selection.videoId,
      serializeReportFilterUrl(filters),
      expectedFingerprint ?? "ungated",
      api,
    ],
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (query.error instanceof SessionExpiredError) onSessionExpired?.();
  }, [onSessionExpired, query.error]);

  const accessDenied = query.error instanceof AccessDeniedError;
  useLayoutEffect(() => {
    if (accessDenied) onAccessChanged?.();
  }, [accessDenied, onAccessChanged]);

  const outOfSync = query.data !== undefined
    && expectedFingerprint !== undefined
    && reportingEnvelopeFingerprint(query.data) !== expectedFingerprint;

  return (
    <section aria-label="Contributing ads" className="video-ads-panel">
      {accessDenied ? (
        <Text aria-label="Report access changed" role="status" size="sm">
          Report access changed. Rechecking your permitted workspace…
        </Text>
      ) : query.isPending ? (
        <div aria-live="polite" className="drilldown-state" role="status">
          <Loader size="xs" /> Loading contributing ads…
        </div>
      ) : query.error ? (
        <AdsError onRetry={() => void query.refetch()} />
      ) : outOfSync ? (
        <Alert color="red" role="alert" title="Ads are out of sync">
          <Stack gap="xs">
            <Text size="sm">
              These ads resolved against a different report snapshot, so they are not shown with this video.
            </Text>
            <Button onClick={() => void query.refetch()} size="compact-sm" variant="light">
              Reload ads
            </Button>
          </Stack>
        </Alert>
      ) : query.data ? (
        <VideoAdsTable
          columnIds={columnIds}
          response={query.data}
          videoMetricCoverageThreshold={videoMetricCoverageThreshold}
        />
      ) : null}
    </section>
  );
}

function AdsError({ onRetry }: { readonly onRetry: () => void }) {
  return (
    <Alert color="red" role="alert" title="Ads could not be loaded">
      <Stack gap="xs">
        <Text size="sm">The video total is still shown above. Try loading its ads again.</Text>
        <Button onClick={onRetry} size="compact-sm" variant="light">
          Retry ads
        </Button>
      </Stack>
    </Alert>
  );
}

function VideoAdsTable({
  columnIds,
  response,
  videoMetricCoverageThreshold,
}: {
  readonly columnIds: readonly ReportMetricId[];
  readonly response: DrilldownResponse;
  readonly videoMetricCoverageThreshold: number;
}) {
  return (
    <>
      {response.cacheStatus === "stale" ? (
        <Alert
          aria-label="Cached drilldown may be out of date"
          color="orange"
          mb="sm"
          role="alert"
          title="Cached drilldown may be out of date"
        >
          Freshness checks are unavailable; these contributing ads may be out of date.
        </Alert>
      ) : null}
      {response.rows.length === 0 ? (
        <Text c="dimmed" role="status" size="sm">No contributing ads matched these filters.</Text>
      ) : (
        <div className="drilldown-table-wrap">
          <table className="drilldown-table">
            <thead>
              <tr>
                <th scope="col">Contributing ad</th>
                {columnIds.map((columnId) => (
                  <th key={columnId} scope="col">{MetricRegistry[columnId].label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {response.rows.map((ad) => (
                <tr key={ad.adId}>
                  <th scope="row">
                    <span className="drilldown-ad-name" title={ad.adName}>{ad.adName}</span>
                    <span className="drilldown-ad-context">
                      {[ad.campaignName, ad.adsetName].filter(Boolean).join(" · ") || "Campaign details unavailable"}
                    </span>
                  </th>
                  {columnIds.map((columnId) => (
                    <MetricCell
                      columnId={columnId}
                      currency={ad.currency}
                      key={columnId}
                      testId={columnId === "spend" ? "drilldown-spend" : undefined}
                      value={ad[columnId]}
                      videoCoverageRate={ad.videoCoverageRate}
                      videoMetricCoverageThreshold={videoMetricCoverageThreshold}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Drilldown API total</th>
                {columnIds.map((columnId) => (
                  <MetricCell
                    columnId={columnId}
                    currency={response.total.currency}
                    key={columnId}
                    testId={columnId === "spend" ? "drilldown-total-spend" : undefined}
                    value={response.total[columnId]}
                    videoCoverageRate={response.total.videoCoverageRate}
                    videoMetricCoverageThreshold={videoMetricCoverageThreshold}
                  />
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </>
  );
}

function MetricCell({
  columnId,
  currency,
  testId,
  value,
  videoCoverageRate,
  videoMetricCoverageThreshold,
}: {
  readonly columnId: ReportMetricId;
  readonly currency: string;
  readonly testId?: string;
  readonly value: number | null;
  readonly videoCoverageRate: number | null;
  readonly videoMetricCoverageThreshold: number;
}) {
  const covered = !isVideoCoverageMetric(columnId)
    || (videoCoverageRate !== null
      && videoCoverageRate >= videoMetricCoverageThreshold);
  const formatted = covered
    ? formatMetricValue(columnId, value, currency)
    : null;
  return (
    <td data-raw-value={value ?? undefined} data-testid={testId}>
      {formatted ?? <NoDataCell />}
    </td>
  );
}
